const MAX_NAME_LENGTH = 64;
const SAFE_SEGMENT_PATTERN = /^[A-Za-z0-9_-]{1,20}$/;

// How long a redirect path waits for its target before it is reported.
export const REDIRECT_WAIT_MS = 3000;

// Routes without parameters, keyed by the hash path.
const STATIC_SCREENS = {
  "/login": { name: "Login", pageType: "LOGIN" },
  "/select-login": { name: "Login > Select", pageType: "LOGIN" },
  "/forgot-password": { name: "Login > Forgot Password", pageType: "LOGIN" },
  "/selectprofile": { name: "Profile Selection", pageType: "PROFILE" },
  "/channelcontrol": { name: "Channel Control", pageType: "OTHER" },
  "/channelcoverage": { name: "Channel Coverage", pageType: "OTHER" },
  "/channellocking": { name: "Channel Locking", pageType: "OTHER" },
  "/dashboard/tvguide": { name: "TV Guide", pageType: "EPG" },
  "/dashboard/search": { name: "Search", pageType: "SEARCH" },
  "/dashboard/records": { name: "Records", pageType: "MY_LIST" },
  "/dashboard/package": { name: "Packages", pageType: "PURCHASE" },
  "/dashboard/contentfilter": { name: "Settings > Content Filter", pageType: "SETTINGS" },
  "/dashboard/settings": { name: "Settings", pageType: "SETTINGS" },
  "/dashboard/language": { name: "Settings > Language", pageType: "SETTINGS" },
  "/dashboard/version": { name: "Settings > Version", pageType: "SETTINGS" },
  "/dashboard/notifications": {
    name: "Settings > Notifications",
    pageType: "SETTINGS",
  },
  "/dashboard/devices": { name: "Settings > Devices", pageType: "SETTINGS" },
  "/dashboard/purchasehistory": {
    name: "Settings > Purchase History",
    pageType: "SETTINGS",
  },
  "/dashboard/suggestioncomplaint": {
    name: "Settings > Suggestion Complaint",
    pageType: "SETTINGS",
  },
  "/dashboard/parentalcontrol": {
    name: "Settings > Parental Control",
    pageType: "SETTINGS",
  },
  "/dashboard/favlockchannel": {
    name: "Settings > Locked Channels",
    pageType: "SETTINGS",
  },
};

// Routes with parameters. The parameter values never go into the name,
// except menuId/categoryId through safeSegment.
const PARAM_SCREENS = [
  {
    pattern: ["forgot-password-change", ":"],
    name: "Login > Reset Password",
    pageType: "LOGIN",
  },
  { pattern: ["profiles", ":"], name: "Profiles", pageType: "PROFILE" },
  { pattern: ["profile", ":"], name: "Profile Settings", pageType: "PROFILE" },
  {
    pattern: ["dashboard", "voddetail", ":"],
    name: "VOD Detail",
    pageType: "DETAIL",
  },
  {
    pattern: ["dashboard", "catchupdetail", ":"],
    name: "Catch-up Detail",
    pageType: "DETAIL",
  },
  {
    pattern: ["dashboard", "program", ":"],
    name: "Program Detail",
    pageType: "DETAIL",
  },
  {
    pattern: ["dashboard", "payment", ":"],
    name: "Payment",
    pageType: "PURCHASE",
  },
  { pattern: ["dashboard", "livetv", ":"], name: "Live TV", pageType: "LIVE" },
  { pattern: ["dashboard", "search", ":"], name: "Search", pageType: "SEARCH" },
  {
    pattern: ["dashboard", "genre", ":", ":"],
    name: "Genre",
    pageType: "CATEGORY",
  },
];

// Menu routes: /dashboard/<section>/:menuId/:categoryId
const MENU_SCREENS = {
  home: { prefix: "Home", pageType: "HOME" },
  catchup: { prefix: "Catch-up", pageType: "CATEGORY" },
};

const safeSegment = (value) =>
  SAFE_SEGMENT_PATTERN.test(value) ? value : ":id";

const matchesPattern = (segments, pattern) =>
  segments.length === pattern.length &&
  pattern.every((part, index) => part === ":" || part === segments[index]);

const getMenuScreen = (segments) => {
  if (segments.length !== 4 || segments[0] !== "dashboard") {
    return null;
  }

  const menu = MENU_SCREENS[segments[1]];

  if (!menu) {
    return null;
  }

  const categoryId = segments[3];
  let name = `${menu.prefix} > Menu:${safeSegment(segments[2])}`;

  if (categoryId !== "all") {
    name += ` > Category:${safeSegment(categoryId)}`;
  }

  return { name: name.slice(0, MAX_NAME_LENGTH), pageType: menu.pageType };
};

// Maps a hash route path to an Atlas screen { name, pageType }.
// Returns null for redirects and unknown paths. Never throws.
export function getScreenForPath(pathname) {
  if (typeof pathname !== "string") {
    return null;
  }

  const segments = pathname.split("/").filter(Boolean);
  const staticScreen = STATIC_SCREENS["/" + segments.join("/")];

  if (staticScreen) {
    return { ...staticScreen };
  }

  const paramScreen = PARAM_SCREENS.find((screen) =>
    matchesPattern(segments, screen.pattern)
  );

  if (paramScreen) {
    return { name: paramScreen.name, pageType: paramScreen.pageType };
  }

  return getMenuScreen(segments);
}

// True for /dashboard/home/:menuId/all, which Home redirects to the first
// category once the categories load.
export function isRedirectPath(pathname) {
  if (typeof pathname !== "string") {
    return false;
  }

  const segments = pathname.split("/").filter(Boolean);

  return (
    segments.length === 4 &&
    segments[0] === "dashboard" &&
    segments[1] === "home" &&
    segments[3] === "all"
  );
}

// Returns track(pathname), which calls report(name, { pageType }) only when
// the screen is known and differs from the last reported one. With
// redirectWaitMs, a redirect path is reported only if no other path follows
// within that time.
export function createScreenTracker(
  report,
  {
    redirectWaitMs = 0,
    setTimer = (fn, ms) => setTimeout(fn, ms),
    clearTimer = (id) => clearTimeout(id),
  } = {}
) {
  let last = null;
  let pending = null;

  const send = (screen, routeKey) => {
    if (
      last &&
      last.name === screen.name &&
      last.pageType === screen.pageType &&
      last.routeKey === routeKey
    ) {
      return;
    }

    last = { ...screen, routeKey };
    report(screen.name, { pageType: screen.pageType });
  };

  return function track(pathname) {
    if (pending !== null) {
      clearTimer(pending);
      pending = null;
    }

    const screen = getScreenForPath(pathname);

    if (!screen) {
      return;
    }

    const segments = pathname.split("/").filter(Boolean);
    const routeKey = getMenuScreen(segments) ? segments.join("/") : undefined;

    if (redirectWaitMs > 0 && isRedirectPath(pathname)) {
      pending = setTimer(() => {
        pending = null;
        send(screen, routeKey);
      }, redirectWaitMs);
      return;
    }

    send(screen, routeKey);
  };
}
